import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Loader2, UserPlus, Gavel, Rocket, FileText, Pencil, Plus, Trash2, Paperclip, Image as ImageIcon, Sparkles, X, RotateCcw, Undo2, AlertTriangle, FileCheck, UploadCloud, History, CreditCard, Clock, CheckCircle2 } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "");
const STEP_LABELS = { submitted: "Submitted", under_review: "Under review", revision_required: "Revision requested", accepted: "Accepted", rejected: "Rejected", proofreading: "Sent to proof-reading", published: "Published", unpublished: "Unpublished", retracted: "Retracted" };
const PAYMENT_LABELS = { "payment:requested": "Payment link sent", "payment:paid": "Payment received", "payment:waived": "Fee waived", "payment:unpaid": "Payment reset to unpaid" };
const stepLabel = (s) => STEP_LABELS[s] || PAYMENT_LABELS[s] || (s?.startsWith("payment:") ? `Payment ${s.split(":")[1]}` : s);
function buildTimeline(paper) {
  let events = (paper.status_history && paper.status_history.length)
    ? paper.status_history.map((h) => ({ label: stepLabel(h.status), at: h.at, by: h.by }))
    : [
        { label: "Submitted", at: paper.created_at },
        ...(paper.status !== "submitted" ? [{ label: stepLabel(paper.status), at: paper.updated_at }] : []),
        ...(paper.payment_updated_at ? [{ label: `Payment ${paper.payment_status}`, at: paper.payment_updated_at }] : []),
        ...(paper.published_at && paper.status === "published" ? [{ label: "Published", at: paper.published_at }] : []),
      ];
  return events.filter((e) => e.at).sort((a, b) => new Date(a.at) - new Date(b.at));
}

const FILTERS = [
  { v: "all", l: "All" },
  { v: "submitted", l: "Submitted" },
  { v: "under_review", l: "Under Review" },
  { v: "revision_required", l: "Revision" },
  { v: "accepted", l: "Accepted" },
  { v: "payment", l: "Payment" },
  { v: "proofreading", l: "Proof-reading" },
  { v: "published", l: "Published" },
  { v: "trash", l: "Trash" },
];

function PaperActions({ paper, reviewers, reload }) {
  const [reviewer, setReviewer] = useState("");
  const [decision, setDecision] = useState("");
  const [note, setNote] = useState("");
  const [vol, setVol] = useState(1);
  const [issue, setIssue] = useState(1);
  const [pages, setPages] = useState("");
  const [busy, setBusy] = useState(false);
  const [retractNote, setRetractNote] = useState(paper.retraction_note || "");
  const [proofing, setProofing] = useState(false);
  const [checklist, setChecklist] = useState(paper.proof_checklist || {});
  const [proofNotes, setProofNotes] = useState(paper.proof_notes || "");
  const [payNote, setPayNote] = useState(paper.payment_note || "");
  const [feeVal, setFeeVal] = useState(paper.fee || paper.journal?.apc || "$600");
  const paymentCleared = ["paid", "waived"].includes(paper.payment_status);
  const setPayment = (status) => doAction(async () => { await api.post(`/papers/${paper.id}/payment-status`, { status, note: payNote }); toast.success(`Payment marked ${status}`); });
  const requestPayment = () => doAction(async () => { await api.post(`/papers/${paper.id}/request-payment`, { fee: feeVal }); toast.success("Payment request emailed to the author"); });

  const CHECK_ITEMS = [["figures", "Figures & tables checked"], ["references", "References verified"], ["metadata", "Metadata & authors correct"]];

  const sendToProofreading = () => doAction(async () => { await api.post(`/papers/${paper.id}/proofread`); toast.success("Moved to proof-reading"); });
  const restoreVersion = (v) => doAction(async () => { await api.post(`/papers/${paper.id}/proof-file/restore`, { url: v.url, name: v.name }); toast.success("Version restored"); });
  const saveProofMeta = () => doAction(async () => { await api.put(`/papers/${paper.id}`, { proof_checklist: checklist, proof_notes: proofNotes }); toast.success("Proof details saved"); });
  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProofing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/papers/${paper.id}/proof-file`, { url: data.url, name: data.filename });
      toast.success("Proof-read file uploaded (new version)");
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setProofing(false); e.target.value = ""; }
  };

  const doAction = async (fn) => {
    setBusy(true);
    try { await fn(); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const unpublish = () => doAction(async () => { await api.post(`/papers/${paper.id}/unpublish`); toast.success("Article unpublished (back to Accepted)"); });
  const retract = () => doAction(async () => { await api.post(`/papers/${paper.id}/retract`, { note: retractNote }); toast.success("Article marked as retracted"); });
  const unretract = () => doAction(async () => { await api.post(`/papers/${paper.id}/unretract`); toast.success("Retraction removed"); });

  const assign = async () => {
    if (!reviewer) return;
    setBusy(true);
    try {
      await api.post(`/papers/${paper.id}/assign-reviewer`, { reviewer_id: reviewer });
      toast.success("Reviewer assigned");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const decide = async () => {
    if (!decision) return;
    setBusy(true);
    try {
      await api.post(`/papers/${paper.id}/decision`, { decision, note });
      toast.success("Decision recorded");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/papers/${paper.id}/publish`, { volume: Number(vol), issue: Number(issue), pages });
      toast.success("Paper published!");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-4" data-testid={`timeline-${paper.id}`}>
        <Label className="flex items-center gap-1.5 mb-3"><Clock className="w-4 h-4 text-primary" /> Activity timeline</Label>
        <ol className="space-y-2.5">
          {buildTimeline(paper).map((e, i) => (
            <li key={i} className="flex items-start gap-3 text-sm" data-testid={`timeline-step-${i}`}>
              <span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" />
              <div className="flex-1 flex items-baseline justify-between gap-3">
                <span className="font-medium text-slate-700 dark:text-slate-300 capitalize">{e.label}{e.by ? <span className="text-slate-400 font-normal"> · {e.by}</span> : null}</span>
                <span className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(e.at)}</span>
              </div>
            </li>
          ))}
        </ol>
      </div>
      <div className="text-sm">
        <p className="text-slate-600 dark:text-slate-400">{paper.abstract}</p>
        <p className="mt-2 text-slate-500">Authors: {paper.authors?.map((a) => a.name).join(", ")}</p>
      </div>

      {paper.reviews?.length > 0 && (
        <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 text-sm">
          <p className="font-semibold mb-2">Reviews</p>
          {paper.reviews.map((rv) => (
            <div key={rv.id} className="border-b last:border-0 border-slate-200 dark:border-slate-800 py-2">
              <span className="font-medium">{rv.reviewer_name}</span> — <StatusBadge status={rv.status} />
              {rv.status === "completed" && <p className="text-slate-500 mt-1">{rv.recommendation?.replace("_", " ")} ({rv.rating}/5): {rv.comments}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="border rounded-lg p-4">
        <Label className="flex items-center gap-1.5 mb-2"><UserPlus className="w-4 h-4" /> Assign reviewer</Label>
        <div className="flex gap-2">
          <Select value={reviewer} onValueChange={setReviewer}>
            <SelectTrigger data-testid="assign-reviewer-select"><SelectValue placeholder="Select reviewer" /></SelectTrigger>
            <SelectContent>
              {reviewers.length === 0 && <div className="px-2 py-1.5 text-sm text-slate-500">No reviewers. Promote a user first.</div>}
              {reviewers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} ({r.email})</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={assign} disabled={busy || !reviewer} data-testid="assign-reviewer-btn" className="rounded-full">Assign</Button>
        </div>
      </div>

      <div className="border rounded-lg p-4">
        <Label className="flex items-center gap-1.5 mb-2"><Gavel className="w-4 h-4" /> Editorial decision</Label>
        <Select value={decision} onValueChange={setDecision}>
          <SelectTrigger data-testid="decision-select"><SelectValue placeholder="Select decision" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="accepted">Accept</SelectItem>
            <SelectItem value="revision_required">Request Revision</SelectItem>
            <SelectItem value="rejected">Reject</SelectItem>
          </SelectContent>
        </Select>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to author (optional)" className="mt-2" rows={2} data-testid="decision-note" />
        <Button onClick={decide} disabled={busy || !decision} data-testid="decision-btn" className="rounded-full mt-2">Record Decision</Button>
      </div>

      {paper.status === "accepted" && (
        <div className="border rounded-lg p-4 space-y-3" data-testid={`payment-box-${paper.id}`}>
          <Label className="flex items-center gap-1.5"><CreditCard className="w-4 h-4 text-emerald-600" /> Article Processing Charge (Payment)</Label>
          <div>
            <Label className="text-xs">Fee payment (editable — defaults to the journal APC)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={feeVal} onChange={(e) => setFeeVal(e.target.value)} placeholder="$600" className="max-w-[160px]" data-testid={`payment-fee-${paper.id}`} />
              <Button onClick={requestPayment} disabled={busy} variant="outline" className="rounded-full gap-1.5" data-testid={`request-payment-${paper.id}`}><Rocket className="w-4 h-4" /> Send payment request</Button>
            </div>
            <p className="text-xs text-slate-400 mt-1">Saves the fee and emails the corresponding author a secure payment link.</p>
          </div>
          <div className={`text-sm rounded-md px-3 py-2 ${paymentCleared ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300" : "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300"}`} data-testid={`payment-status-${paper.id}`}>
            Status: <strong className="capitalize">{paper.payment_status || "unpaid"}</strong>{paper.payment_note ? ` — ${paper.payment_note}` : ""}
          </div>
          <div className="text-xs space-y-1" data-testid={`payment-events-${paper.id}`}>
            {paper.payment_requested_at && <p className="flex items-center gap-1.5 text-sky-600 dark:text-sky-400"><CheckCircle2 className="w-3.5 h-3.5" /> Payment link sent — {fmtDate(paper.payment_requested_at)}</p>}
            {paper.payment_status === "paid" && <p className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> Payment received — {fmtDate(paper.payment_updated_at)}</p>}
            {paper.payment_status === "waived" && <p className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> Fee waived — {fmtDate(paper.payment_updated_at)}</p>}
          </div>
          <Textarea value={payNote} onChange={(e) => setPayNote(e.target.value)} rows={2} placeholder="Payment reference / invoice note (optional)" data-testid={`payment-note-${paper.id}`} />
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setPayment("paid")} disabled={busy} className="rounded-full gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid={`mark-paid-${paper.id}`}><CreditCard className="w-4 h-4" /> Paid</Button>
            <Button onClick={() => setPayment("waived")} disabled={busy} variant="outline" className="rounded-full" data-testid={`mark-waived-${paper.id}`}>Waive fee</Button>
            {paymentCleared && <Button onClick={() => setPayment("unpaid")} disabled={busy} variant="ghost" className="rounded-full text-slate-500" data-testid={`mark-unpaid-${paper.id}`}>Reset to unpaid</Button>}
          </div>
        </div>
      )}

      {paper.status === "accepted" && (
        <div className="border rounded-lg p-4 space-y-3" data-testid={`proofread-start-${paper.id}`}>
          <Label className="flex items-center gap-1.5"><FileCheck className="w-4 h-4 text-indigo-600" /> Proof-reading</Label>
          <p className="text-sm text-slate-500">Upload the proof-read file (PDF/DOC/DOCX), then send the manuscript to proof-reading so the author can review and approve it.</p>
          {!paymentCleared && <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1.5" data-testid={`proof-payment-gate-${paper.id}`}><AlertTriangle className="w-4 h-4" /> Clear the Article Processing Charge above before sending to proof-reading.</p>}
          {paper.file_name && <p className="text-sm flex items-center gap-1.5"><FileText className="w-4 h-4 text-primary" /> Proof file: <span className="font-medium">{paper.file_name}</span></p>}
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-sm rounded-full border border-slate-200 dark:border-slate-700 px-4 py-2 cursor-pointer hover:border-primary hover:text-primary transition-colors" data-testid={`upload-proof-start-${paper.id}`}>
              {proofing ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} {paper.file_name ? "Replace proof file" : "Upload proof-read file"}
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={uploadProof} />
            </label>
            <Button onClick={sendToProofreading} disabled={busy || !paymentCleared} variant="outline" className="rounded-full gap-1.5" data-testid={`send-proofread-${paper.id}`}><FileCheck className="w-4 h-4" /> Send to Proof-reading</Button>
          </div>
        </div>
      )}

      {paper.status === "proofreading" && (
        <div className="border rounded-lg p-4 bg-indigo-50/40 dark:bg-indigo-950/20 space-y-4" data-testid={`proofread-box-${paper.id}`}>
          <Label className="flex items-center gap-1.5"><FileCheck className="w-4 h-4 text-indigo-600" /> Proof-reading</Label>
          <p className="text-sm text-slate-600 dark:text-slate-400">Upload the final proof-read file (versioned), add copy-edit notes for the author, and tick the checklist before publishing.</p>

          <div className={`text-sm rounded-md px-3 py-2 ${paper.proof_approved ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300" : "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300"}`} data-testid={`proof-approval-${paper.id}`}>
            {paper.proof_approved ? "✓ Author has approved the current proof" : "Awaiting author approval of the proof"}
          </div>

          {paper.file_name && <p className="text-sm flex items-center gap-1.5"><FileText className="w-4 h-4 text-primary" /> Current file: <span className="font-medium">{paper.file_name}</span></p>}
          <label className="inline-flex items-center gap-1.5 text-sm rounded-full border border-slate-200 dark:border-slate-700 px-4 py-2 cursor-pointer hover:border-primary hover:text-primary transition-colors" data-testid={`upload-proof-${paper.id}`}>
            {proofing ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Upload proof-read file
            <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={uploadProof} />
          </label>

          {paper.proof_versions?.length > 0 && (
            <div data-testid={`proof-versions-${paper.id}`}>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Version history</p>
              <ul className="space-y-1.5">
                {paper.proof_versions.map((v, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm" data-testid={`proof-version-${i}`}>
                    <History className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="truncate flex-1">v{i + 1} · {v.name || "file"} <span className="text-xs text-slate-400">({new Date(v.uploaded_at).toLocaleDateString()})</span></span>
                    <Button variant="ghost" size="sm" className="h-7 rounded-full gap-1" onClick={() => restoreVersion(v)} data-testid={`restore-version-${i}`}><RotateCcw className="w-3.5 h-3.5" /> Use</Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Pre-publish checklist</p>
            <div className="space-y-1.5">
              {CHECK_ITEMS.map(([k, lbl]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <input type="checkbox" checked={!!checklist[k]} onChange={() => setChecklist((c) => ({ ...c, [k]: !c[k] }))} data-testid={`proof-check-${k}`} /> {lbl}
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Copy-edit notes for the author</Label>
            <Textarea value={proofNotes} onChange={(e) => setProofNotes(e.target.value)} rows={2} className="mt-1" placeholder="e.g. Please confirm the corrected affiliation for author 2…" data-testid={`proof-notes-${paper.id}`} />
            <Button onClick={saveProofMeta} disabled={busy} variant="outline" className="rounded-full gap-1.5 mt-2" data-testid={`save-proof-meta-${paper.id}`}><Pencil className="w-4 h-4" /> Save checklist & notes</Button>
          </div>
        </div>
      )}

      {["accepted", "under_review", "revision_required", "proofreading"].includes(paper.status) && (
        <div className="border rounded-lg p-4 bg-emerald-50/50 dark:bg-emerald-950/20">
          <Label className="flex items-center gap-1.5 mb-2"><Rocket className="w-4 h-4" /> Publish</Label>
          <div className="grid grid-cols-3 gap-2">
            <div><span className="text-xs text-slate-500">Volume</span><Input type="number" value={vol} onChange={(e) => setVol(e.target.value)} data-testid="publish-volume" /></div>
            <div><span className="text-xs text-slate-500">Issue</span><Input type="number" value={issue} onChange={(e) => setIssue(e.target.value)} data-testid="publish-issue" /></div>
            <div><span className="text-xs text-slate-500">Pages</span><Input value={pages} onChange={(e) => setPages(e.target.value)} placeholder="1-12" data-testid="publish-pages" /></div>
          </div>
          <Button onClick={publish} disabled={busy} data-testid="publish-btn" className="rounded-full mt-3 bg-emerald-600 hover:bg-emerald-700">Publish Paper</Button>
        </div>
      )}

      {paper.status === "published" && (
        <div className="border rounded-lg p-4 space-y-3" data-testid={`published-actions-${paper.id}`}>
          <Label className="flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-amber-600" /> Published article controls</Label>
          {paper.retracted && (
            <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 p-2.5 text-sm text-red-700 dark:text-red-300">
              This article is marked <strong>RETRACTED</strong>{paper.retraction_note ? `: ${paper.retraction_note}` : "."}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={unpublish} disabled={busy} variant="outline" className="rounded-full gap-1.5" data-testid={`unpublish-${paper.id}`}><Undo2 className="w-4 h-4" /> Unpublish</Button>
            {paper.retracted
              ? <Button onClick={unretract} disabled={busy} variant="outline" className="rounded-full gap-1.5" data-testid={`unretract-${paper.id}`}>Remove retraction</Button>
              : null}
          </div>

          <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 space-y-2.5" data-testid={`published-replace-pdf-${paper.id}`}>
            <p className="text-sm font-medium flex items-center gap-1.5"><FileText className="w-4 h-4 text-primary" /> Final published PDF</p>
            <p className="text-xs text-slate-500">Replace the live PDF served on the article's DOI / download link. The new file goes live immediately at the same link.</p>
            {paper.file_name && <p className="text-sm">Current file: <span className="font-medium">{paper.file_name}</span></p>}
            <label className="inline-flex items-center gap-1.5 text-sm rounded-full border border-slate-200 dark:border-slate-700 px-4 py-2 cursor-pointer hover:border-primary hover:text-primary transition-colors" data-testid={`replace-pdf-${paper.id}`}>
              {proofing ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Upload replacement PDF
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={uploadProof} />
            </label>
            {paper.proof_versions?.length > 0 && (
              <div data-testid={`published-versions-${paper.id}`}>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 mt-1">Version history</p>
                <ul className="space-y-1.5">
                  {paper.proof_versions.map((v, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm" data-testid={`published-version-${i}`}>
                      <History className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="truncate flex-1">v{i + 1} · {v.name || "file"} <span className="text-xs text-slate-400">({new Date(v.uploaded_at).toLocaleDateString()})</span></span>
                      <Button variant="ghost" size="sm" className="h-7 rounded-full gap-1" onClick={() => restoreVersion(v)} data-testid={`published-restore-version-${i}`}><RotateCcw className="w-3.5 h-3.5" /> Use</Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {!paper.retracted && (
            <div>
              <Textarea value={retractNote} onChange={(e) => setRetractNote(e.target.value)} placeholder="Reason for retraction (shown publicly on the article)" rows={2} data-testid={`retract-note-${paper.id}`} />
              <Button onClick={retract} disabled={busy} variant="destructive" className="rounded-full mt-2 gap-1.5" data-testid={`retract-${paper.id}`}><AlertTriangle className="w-4 h-4" /> Mark as Retracted</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ARTICLE_TYPES = ["Research Article", "Review", "Case Study", "Short Communication", "Editorial", "Perspective"];

function EditPaperForm({ paper, reload }) {
  const [f, setF] = useState({
    title: paper.title || "",
    abstract: paper.abstract || "",
    keywords: (paper.keywords || []).join(", "),
    article_type: paper.article_type || "Research Article",
    doi: paper.doi || "",
    volume: paper.volume ?? "",
    issue: paper.issue ?? "",
    pages: paper.pages || "",
    published_at: paper.published_at ? paper.published_at.slice(0, 10) : "",
    citations: paper.citations ?? 0,
  });
  const [authors, setAuthors] = useState(
    paper.authors?.length ? paper.authors.map((a) => ({ ...a })) : [{ name: "", affiliation: "", email: "", is_corresponding: true }]
  );
  const [attachments, setAttachments] = useState(paper.attachments || []);
  const [busy, setBusy] = useState(false);
  const [doiBusy, setDoiBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const genDoi = async () => {
    setDoiBusy(true);
    try {
      const { data } = await api.post(`/papers/${paper.id}/generate-doi`);
      setF((prev) => ({ ...prev, doi: data.doi }));
      toast.success("DOI generated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setDoiBusy(false); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const kind = file.type.startsWith("image/") ? "image" : "pdf";
        setAttachments((a) => [...a, { url: data.url, name: data.filename, kind }]);
      }
      toast.success("Attached");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setUploading(false); e.target.value = ""; }
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setAuthor = (i, k, v) => setAuthors(authors.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));
  const addAuthor = () => setAuthors([...authors, { name: "", affiliation: "", email: "", is_corresponding: false }]);
  const removeAuthor = (i) => setAuthors(authors.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!f.title.trim() || !f.abstract.trim()) { toast.error("Title and abstract are required."); return; }
    setBusy(true);
    try {
      const payload = {
        title: f.title,
        abstract: f.abstract,
        keywords: f.keywords.split(",").map((s) => s.trim()).filter(Boolean),
        article_type: f.article_type,
        doi: f.doi || null,
        pages: f.pages,
        authors: authors.filter((a) => a.name.trim()),
      };
      if (f.volume !== "") payload.volume = Number(f.volume);
      if (f.issue !== "") payload.issue = Number(f.issue);
      if (f.citations !== "") payload.citations = Number(f.citations);
      if (f.published_at) payload.published_at = new Date(f.published_at).toISOString();
      payload.attachments = attachments;
      await api.put(`/papers/${paper.id}`, payload);
      toast.success("Article details updated");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid={`edit-paper-form-${paper.id}`}>
      <div><Label>Title</Label><Input value={f.title} onChange={set("title")} className="mt-1" data-testid="edit-paper-title" /></div>
      <div><Label>Abstract</Label><Textarea value={f.abstract} onChange={set("abstract")} rows={5} className="mt-1" data-testid="edit-paper-abstract" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Article type</Label>
          <Select value={f.article_type} onValueChange={(v) => setF({ ...f, article_type: v })}>
            <SelectTrigger className="mt-1" data-testid="edit-paper-type"><SelectValue /></SelectTrigger>
            <SelectContent>{ARTICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Keywords (comma-separated)</Label><Input value={f.keywords} onChange={set("keywords")} className="mt-1" data-testid="edit-paper-keywords" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>DOI</Label>
          <div className="flex gap-2 mt-1">
            <Input value={f.doi} onChange={set("doi")} placeholder="10.9999/…" data-testid="edit-paper-doi" />
            <Button type="button" variant="outline" className="rounded-full shrink-0 px-3" onClick={genDoi} disabled={doiBusy} title="Auto-generate DOI" data-testid="edit-paper-gen-doi">
              {doiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        <div><Label>Publication date</Label><Input type="date" value={f.published_at} onChange={set("published_at")} className="mt-1" data-testid="edit-paper-date" /></div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><Label>Volume</Label><Input type="number" value={f.volume} onChange={set("volume")} className="mt-1" data-testid="edit-paper-volume" /></div>
        <div><Label>Issue</Label><Input type="number" value={f.issue} onChange={set("issue")} className="mt-1" data-testid="edit-paper-issue" /></div>
        <div><Label>Pages</Label><Input value={f.pages} onChange={set("pages")} className="mt-1" placeholder="1-12" data-testid="edit-paper-pages" /></div>
      </div>
      <div><Label>Citation count (auto-tracked; override here)</Label><Input type="number" min="0" value={f.citations} onChange={set("citations")} className="mt-1" data-testid="edit-paper-citations" /></div>

      <div className="border rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <Label>Authors</Label>
          <Button variant="outline" size="sm" className="rounded-full gap-1 h-7" onClick={addAuthor} data-testid="edit-paper-add-author"><Plus className="w-3.5 h-3.5" /> Add</Button>
        </div>
        <div className="space-y-2">
          {authors.map((a, i) => (
            <div key={i} className="flex items-start gap-2" data-testid={`edit-paper-author-${i}`}>
              <div className="grid grid-cols-2 gap-2 flex-1">
                <Input value={a.name} onChange={(e) => setAuthor(i, "name", e.target.value)} placeholder="Full name" className="h-8" data-testid={`edit-author-name-${i}`} />
                <Input value={a.affiliation || ""} onChange={(e) => setAuthor(i, "affiliation", e.target.value)} placeholder="Affiliation" className="h-8" />
              </div>
              <label className="flex items-center gap-1 text-xs text-slate-500 shrink-0 mt-2" title="Corresponding author">
                <input type="checkbox" checked={!!a.is_corresponding} onChange={(e) => setAuthor(i, "is_corresponding", e.target.checked)} data-testid={`edit-author-corr-${i}`} /> Corr.
              </label>
              {authors.length > 1 && <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => removeAuthor(i)} data-testid={`edit-author-remove-${i}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
            </div>
          ))}
        </div>
      </div>

      <div className="border rounded-lg p-3" data-testid={`attachments-section-${paper.id}`}>
        <div className="flex items-center justify-between mb-2">
          <Label>Figures &amp; supplementary files</Label>
          <label className="inline-flex items-center gap-1 text-xs rounded-full border border-slate-200 dark:border-slate-700 px-3 py-1.5 cursor-pointer hover:border-primary hover:text-primary transition-colors" data-testid="edit-paper-attach">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} Attach
            <input type="file" accept="image/*,.pdf" multiple className="hidden" onChange={onUpload} />
          </label>
        </div>
        {attachments.length === 0 ? (
          <p className="text-xs text-slate-400">No figures or files attached. Images display inline on the article; PDFs appear as download links.</p>
        ) : (
          <ul className="space-y-1.5">
            {attachments.map((at, i) => (
              <li key={i} className="flex items-center gap-2 text-sm" data-testid={`attachment-${i}`}>
                {at.kind === "image" ? <ImageIcon className="w-4 h-4 text-primary shrink-0" /> : <FileText className="w-4 h-4 text-primary shrink-0" />}
                <span className="truncate flex-1">{at.name}</span>
                <button type="button" onClick={() => setAttachments(attachments.filter((_, idx) => idx !== i))} data-testid={`attachment-remove-${i}`}><X className="w-4 h-4 text-slate-400 hover:text-destructive" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <DialogFooter>
        <Button onClick={save} disabled={busy} className="rounded-full gap-1.5" data-testid="edit-paper-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Pencil className="w-4 h-4" /> Save Changes</>}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function EditorialPanel() {
  const [papers, setPapers] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [exporting, setExporting] = useState(false);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await api.get("/editorial/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `editorial-report_${fromDate || "all"}_to_${toDate || "all"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel report downloaded");
    } catch (e) {
      toast.error("Could not generate the report. Please try again.");
    } finally {
      setExporting(false);
    }
  };
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    const url = filter === "trash" ? "/trash/papers" : filter === "all" ? "/editorial/papers" : `/editorial/papers?status=${filter}`;
    return api.get(url).then(({ data }) => setPapers(data)).finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { setLoading(true); reload(); }, [reload]);
  useEffect(() => { api.get("/users?role=reviewer").then(({ data }) => setReviewers(data)).catch(() => {}); }, []);

  const del = async (p) => {
    if (!window.confirm(`Move "${p.title}" to Trash? Published or not, it will be hidden from the site and can be restored within 30 days.`)) return;
    try { await api.delete(`/papers/${p.id}`); toast.success("Moved to Trash"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const restore = async (p) => {
    try { await api.post(`/trash/papers/${p.id}/restore`); toast.success("Paper restored"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const purge = async (p) => {
    if (!window.confirm(`Permanently delete "${p.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/trash/papers/${p.id}`); toast.success("Deleted permanently"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="editorial-panel">
      <h1 className="font-serif font-bold text-2xl text-slate-900 dark:text-slate-100 mb-1">Editorial Workflow</h1>
      <p className="text-slate-500 text-sm mb-5">Manage submissions through peer review to publication.</p>

      <div className="flex flex-wrap items-end gap-3 mb-5 rounded-lg border border-slate-200 dark:border-slate-800 p-4" data-testid="export-toolbar">
        <div>
          <Label className="text-xs">From date</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1 w-[160px]" data-testid="export-from-date" />
        </div>
        <div>
          <Label className="text-xs">To date</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1 w-[160px]" data-testid="export-to-date" />
        </div>
        <Button onClick={exportExcel} disabled={exporting} className="rounded-full gap-2" data-testid="export-excel-btn">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Generate Excel report
        </Button>
        <span className="text-xs text-slate-400">Leave dates empty to export all records (by submission date).</span>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-5">
        <TabsList className="flex-wrap h-auto">
          {FILTERS.map((f) => <TabsTrigger key={f.v} value={f.v} data-testid={`filter-${f.v}`}>{f.l}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : papers.length === 0 ? (
        <div className="text-center py-20 bg-card rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
          <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">{filter === "trash" ? "Trash is empty." : "No manuscripts in this stage."}</p>
        </div>
      ) : filter === "trash" ? (
        <div className="space-y-3">
          <p className="text-xs text-slate-400">Deleted papers are kept for 30 days, then removed automatically.</p>
          {papers.map((p) => (
            <div key={p.id} data-testid={`trash-paper-${p.id}`} className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1.5"><StatusBadge status={p.status} /><span className="font-mono text-xs text-amber-600">{p.submission_id}</span></div>
                <h3 className="font-serif font-bold text-lg text-slate-900 dark:text-slate-100 truncate">{p.title}</h3>
                <p className="text-sm text-slate-500 mt-1">Deleted {p.deleted_at ? new Date(p.deleted_at).toLocaleDateString() : ""}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" className="rounded-full gap-1.5" onClick={() => restore(p)} data-testid={`restore-paper-${p.id}`}><RotateCcw className="w-4 h-4" /> Restore</Button>
                <Button variant="outline" size="icon" className="rounded-full text-destructive hover:bg-red-50 dark:hover:bg-red-950/30" onClick={() => purge(p)} data-testid={`purge-paper-${p.id}`} title="Delete permanently"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {papers.map((p) => (
            <div key={p.id} data-testid={`editorial-paper-${p.id}`} className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap"><StatusBadge status={p.status} />{p.status === "accepted" && <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border ${["paid", "waived"].includes(p.payment_status) ? "text-emerald-700 border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30" : "text-amber-700 border-amber-200 bg-amber-50 dark:bg-amber-950/30"}`} data-testid={`payment-badge-${p.id}`}>{["paid", "waived"].includes(p.payment_status) ? (p.payment_status === "waived" ? "FEE WAIVED" : "PAID") : (p.payment_requested_at ? "LINK SENT" : "PAYMENT DUE")}</span>}{p.status === "accepted" && p.fee && <span className="text-xs text-slate-400" data-testid={`row-fee-${p.id}`}>Fee {p.fee}</span>}{p.retracted && <span className="text-[11px] font-semibold text-red-600 border border-red-200 rounded-full px-2 py-0.5">RETRACTED</span>}<span className="font-mono text-xs text-amber-600">{p.submission_id}</span><span className="text-xs text-slate-400">{p.journal?.title}</span></div>
                <h3 className="font-serif font-bold text-lg text-slate-900 dark:text-slate-100 truncate">{p.title}</h3>
                <p className="text-sm text-slate-500 mt-1">{p.authors?.map((a) => a.name).join(", ")} · {p.reviews?.length || 0} review(s)</p>
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1.5" data-testid={`row-dates-${p.id}`}><Clock className="w-3.5 h-3.5" /> Submitted {fmtDate(p.created_at)} · {stepLabel(p.status)} {fmtDate(p.updated_at || p.created_at)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="rounded-full" data-testid={`manage-paper-${p.id}`}>Manage</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-serif text-left">{p.title}</DialogTitle></DialogHeader>
                    <Tabs defaultValue="workflow" className="mt-2">
                      <TabsList>
                        <TabsTrigger value="workflow" data-testid={`tab-workflow-${p.id}`}>Workflow</TabsTrigger>
                        <TabsTrigger value="edit" data-testid={`tab-edit-${p.id}`}>Edit Details</TabsTrigger>
                      </TabsList>
                      <TabsContent value="workflow" className="mt-4"><PaperActions paper={p} reviewers={reviewers} reload={reload} /></TabsContent>
                      <TabsContent value="edit" className="mt-4"><EditPaperForm paper={p} reload={reload} /></TabsContent>
                    </Tabs>
                  </DialogContent>
                </Dialog>
                <Button variant="outline" size="icon" className="rounded-full text-destructive hover:bg-red-50 dark:hover:bg-red-950/30" onClick={() => del(p)} data-testid={`delete-paper-${p.id}`} title="Delete paper">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
